// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import {
  FilterMenuRow,
  VirtualizedNestedFilterMenu,
} from '@/components/virtualized-nested-filter-menu';
import { useStableValue } from '@/hooks/use-stable-value';
import { useLocations } from '@/location/hooks/use-locations';
import { isSameStringList } from '@/utilities/is-same-string-list';
import { groupBy, sortBy, uniq, without } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef } from 'react';

const DEFAULT_LABEL = 'Location';
const INVENTORY_CHIP_LABEL = 'Inventory';
const NO_LOCATIONS_MESSAGE = 'No locations found';
const NO_MATCHES_MESSAGE = 'No matching locations';

interface Props {
  /**
   * An array of strings representing the currently selected location IDs.
   */
  value: string[];

  /**
   * The label for the filter menu.
   *
   * @default "Location"
   */
  label?: string;

  /**
   * A callback fired when the selected location IDs change.
   */
  onChange: (value: string[]) => void;
}

/**
 * A controlled filter menu for selecting zero or more locations from the remote
 * API. The locations are grouped by customer / manufacturer, and selecting a
 * group will select all of the locations in that group.
 * Uses virtualization for performance with large location lists.
 */
export function LocationFilterMenu(props: Props) {
  const { user } = useCurrentUser();
  const { locations } = useLocations();

  const label = props.label ?? DEFAULT_LABEL;

  // Selection is deliberately absent here, so the list survives a toggle
  // instead of being rebuilt by it
  const options = useMemo(
    () =>
      locations.map(({ id, name, customer, isInventory }) => ({
        id,
        label: name,
        group: customer?.name ?? user.organization.name,
        isInventory,
      })),
    [locations, user.organization.name]
  );

  // Callers pass the selection as a fresh literal, so it is held at its last
  // content — which is what lets the row builder below stay memoized
  const value = useStableValue(props.value, isSameStringList);

  // A set, not the array: membership is asked once per option per render
  const selected = useMemo(() => new Set(value), [value]);

  // Read through a ref so a caller's inline handler does not rebuild every row.
  // A row only ever calls it from a click, long after the effect has landed.
  const onChangeRef = useRef(props.onChange);

  useEffect(() => {
    onChangeRef.current = props.onChange;
  });

  const count = useMemo(
    () => options.filter((option) => selected.has(option.id)).length,
    [options, selected]
  );

  // A group header selects only the locations visible beneath it, so toggling
  // one during a search never reaches a row the user cannot see.
  const getRows = useCallback(
    (searchTerm: string): FilterMenuRow[] => {
      const handleChangeGroup = (groupIds: string[], isActive: boolean) => {
        if (isActive) {
          onChangeRef.current(uniq([...value, ...groupIds]));
          return;
        }

        onChangeRef.current(without(value, ...groupIds));
      };

      const handleChangeOption = (optionId: string, isActive: boolean) => {
        if (isActive) {
          onChangeRef.current([...value, optionId]);
          return;
        }

        onChangeRef.current(without(value, optionId));
      };

      const lowerSearch = searchTerm.trim().toLowerCase();
      const filtered = lowerSearch
        ? options.filter(
            (option) =>
              option.label.toLowerCase().includes(lowerSearch) ||
              option.group.toLowerCase().includes(lowerSearch)
          )
        : options;

      const groups = groupBy(
        sortBy(filtered, (option) => option.label),
        'group'
      );
      const sortedLabels = sortBy(Object.keys(groups), (g) => g.toLowerCase());

      const rows: FilterMenuRow[] = [];

      sortedLabels.forEach((groupLabel, index) => {
        const groupOptions = groups[groupLabel];
        const activeCount = groupOptions.filter(({ id }) =>
          selected.has(id)
        ).length;
        const groupIds = groupOptions.map(({ id }) => id);

        if (index > 0) {
          rows.push({ kind: 'divider', key: `divider:${groupLabel}` });
        }

        rows.push({
          kind: 'option',
          key: `group:${groupLabel}`,
          label: groupLabel,
          isEmphasized: true,
          isActive: activeCount === groupOptions.length,
          isIndeterminate: activeCount > 0 && activeCount < groupOptions.length,
          onChange: (isActive) => handleChangeGroup(groupIds, isActive),
        });

        groupOptions.forEach((option) => {
          rows.push({
            kind: 'option',
            key: `location:${option.id}`,
            level: 1,
            label: option.label,
            chip: option.isInventory ? INVENTORY_CHIP_LABEL : undefined,
            isActive: selected.has(option.id),
            onChange: (isActive) => handleChangeOption(option.id, isActive),
          });
        });
      });

      if (rows.length === 0) {
        rows.push({
          kind: 'message',
          key: 'no-results',
          text: lowerSearch === '' ? NO_LOCATIONS_MESSAGE : NO_MATCHES_MESSAGE,
        });
      }

      return rows;
    },
    [options, selected, value]
  );

  return (
    <VirtualizedNestedFilterMenu
      label={label}
      count={count}
      searchPlaceholder="Search locations..."
      getRows={getRows}
    />
  );
}
